import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { Check, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { isSlideLive, type BlockContentMap } from "@veo/types";
import { cn } from "@veo/ui";
import { ResponsiveImage } from "../components/ResponsiveImage";
import type { SectionProps } from "./SectionRenderer";

const HEIGHTS: Record<string, string> = {
  standard: "min-h-[32rem] py-24 md:py-32",
  tall: "min-h-[38rem] py-28 md:min-h-[42rem] md:py-36",
  fullscreen: "min-h-[calc(100dvh-4rem)] py-28",
};

const FOCAL: Record<string, string> = {
  center: "object-center",
  top: "object-top",
  bottom: "object-bottom",
  left: "object-left",
  right: "object-right",
};

/**
 * The hero slider.
 *
 * Built to the carousel pattern rather than the tabs one: slides are content of
 * equal standing, so every slide stays in the DOM and inactive ones are hidden
 * with `inert` — which removes them from the tab order and the accessibility
 * tree without removing them from the page. A screen reader announces the
 * change through the live region; it does not have the rug pulled from under it.
 *
 * Autoplay is on by default at eight seconds. WCAG 2.2.2 requires auto-moving
 * content to be pausable, so it stops on hover, stops on focus, exposes a
 * visible stop control, and never starts for a visitor who has asked for
 * reduced motion. An editor can set the interval to zero to turn it off.
 */
export default function HeroSliderBlock({
  content,
  settings,
}: SectionProps<BlockContentMap["heroSlider"]>) {
  /**
   * Only slides that are live right now.
   *
   * Filtered here rather than server-side because section content is generic
   * JSON on the API and block-aware filtering there would put one block's
   * rules into the resolver. Computed once per render on the server, so a
   * scheduled slide appears and expires with the next request rather than
   * needing a deploy.
   *
   * `useState(() => new Date())` rather than `new Date()` in the render body:
   * a fresh Date every render changes the memo key on every pass, and that is
   * how a render loop starts.
   */
  const [now] = useState(() => new Date());
  const slides = useMemo(
    () => content.slides.filter((slide) => isSlideLive(slide, now)),
    [content.slides, now],
  );

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const regionId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  const count = slides.length;
  const autoplay = content.autoplaySeconds > 0;

  const goTo = useCallback(
    (next: number) => {
      if (count === 0) return;
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  /**
   * Advance on a timer.
   *
   * `prefers-reduced-motion` disables it outright rather than slowing it: for a
   * vestibular trigger, a slower unrequested movement is still an unrequested
   * movement.
   *
   * `index` is in the dependency list on purpose, so any manual move restarts
   * the clock. Without it the interval keeps its original schedule and a
   * visitor who clicks "next" can be auto-advanced again a moment later —
   * which reads as the carousel fighting them.
   */
  useEffect(() => {
    if (!autoplay || paused || count < 2) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const timer = setTimeout(() => {
      setIndex((current) => (current + 1) % count);
    }, content.autoplaySeconds * 1_000);

    return () => clearTimeout(timer);
  }, [autoplay, paused, count, index, content.autoplaySeconds]);

  // Left and right arrows move between slides when the carousel has focus,
  // which is what a keyboard user tries first.
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      goTo(index + 1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      goTo(index - 1);
    }
  };

  if (count === 0) return null;

  const single = count === 1;

  return (
    <section
      id={settings.anchorId || undefined}
      ref={containerRef}
      aria-roledescription={single ? undefined : "carousel"}
      aria-label={single ? undefined : "Featured"}
      onKeyDown={single ? undefined : onKeyDown}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      className={cn("relative overflow-hidden bg-navy-950", settings.customClassName)}
    >
      {/*
        Every slide occupies the same single grid cell rather than the active
        one sitting in flow while the rest are taken out of it.

        With the old arrangement the container was only ever as tall as the
        slide being shown, so the whole page below it jumped each time a slide
        with more copy — or a second button — came around. A grid cell is sized
        by its tallest occupant, so the height is now that of the longest slide
        and constant for the life of the block. `items-stretch` (the default)
        then makes every slide fill it, and each slide's own `justify-center`
        keeps its copy optically centred whatever its length.

        Deliberately CSS rather than measuring the slides in JavaScript: this is
        correct on first paint, before hydration, and stays correct through
        resize and font loading without a single listener.
      */}
      <div className="relative grid">
        {slides.map((slide, slideIndex) => {
          const active = slideIndex === index;

          return (
            <div
              key={`${slide.heading}-${slideIndex}`}
              // `inert` keeps an off-screen slide out of the tab order and the
              // accessibility tree. Without it, a keyboard user tabs into
              // buttons they cannot see.
              {...(active ? {} : { inert: "" as unknown as boolean })}
              aria-hidden={!active}
              aria-roledescription={single ? undefined : "slide"}
              aria-label={single ? undefined : `${slideIndex + 1} of ${count}`}
              className={cn(
                // Same cell for every slide; `relative` anchors the image layer
                // inside it.
                "relative col-start-1 row-start-1",
                "transition-opacity duration-700 motion-reduce:transition-none",
                active ? "z-10 opacity-100" : "pointer-events-none z-0 opacity-0",
              )}
            >
              <div className="absolute inset-0">
                <ResponsiveImage
                  image={slide.image}
                  // The first slide is the largest element in the viewport, so
                  // it loads eagerly; the rest wait.
                  priority={slideIndex === 0}
                  sizes="100vw"
                  className={cn(
                    "size-full object-cover",
                    FOCAL[slide.focalPoint] ?? FOCAL.center,
                    // Mirrors the photograph only. Applied to the image element
                    // rather than its wrapper so the gradient overlay, copy and
                    // buttons above it are unaffected.
                    slide.flipHorizontal && "-scale-x-100",
                  )}
                />
                {/*
                  A two-stop gradient rather than a flat wash: it keeps the copy
                  side readable while letting the photograph stay a photograph
                  on the other, which flat overlays flatten into a grey field.
                */}
                <div
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/70 to-navy-950/20"
                  style={{ opacity: slide.overlayOpacity / 100 }}
                />
                <div
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-transparent to-navy-950/30"
                />
              </div>

              {/*
                `h-full` is what makes the slides read as level.

                The grid above already gives every slide the same frame, but
                this box only claimed its own natural height inside it — so a
                slide with less copy centred its text within a shorter box that
                started at the top of the frame, and its heading sat tens of
                pixels above the heading of the longest slide. Filling the frame
                means `justify-center` centres every slide's copy against the
                same edges, and the eye stops seeing the text step up and down
                as the carousel turns.

                The min-height stays as the floor for a slider whose slides are
                all short; it simply no longer decides where the copy sits.
              */}
              <div
                className={cn(
                  "container-site relative flex h-full flex-col justify-center",
                  HEIGHTS[content.height] ?? HEIGHTS.tall,
                )}
              >
                <div className="max-w-2xl">
                  {slide.eyebrow && (
                    <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.2em] text-gold-300">
                      <span aria-hidden className="h-px w-8 bg-gold-500" />
                      {slide.eyebrow}
                    </p>
                  )}

                  {/*
                    An `h1` for the first slide only; the rest are `h2`.

                    Every slide used to be an `h1`, which put three of them in
                    the homepage's markup. A crawler reads the DOM, not the
                    carousel — it does not know that two of the three are
                    off-screen — so the page announced three competing subjects
                    and none of them as the one. The classes are identical, so
                    nothing about the slider changes visually; only the outline
                    does. The first slide holds the `h1` rather than the active
                    one because the heading a crawler reads should not depend on
                    where an auto-advancing carousel happened to stop.
                  */}
                  {slideIndex === 0 ? (
                    <h1 className="mt-5 text-6xl text-cream-50">{slide.heading}</h1>
                  ) : (
                    <h2 className="mt-5 text-6xl text-cream-50">{slide.heading}</h2>
                  )}

                  {slide.subheading && (
                    <p className="mt-6 max-w-xl text-lg leading-relaxed text-navy-100 md:text-xl">
                      {slide.subheading}
                    </p>
                  )}

                  {slide.buttons.length > 0 && (
                    <div className="mt-9 flex flex-wrap gap-3">
                      {slide.buttons.map((button, buttonIndex) => (
                        <Link
                          key={button.href + button.label}
                          to={button.href}
                          {...(button.opensInNewTab
                            ? { target: "_blank", rel: "noopener noreferrer" }
                            : {})}
                          className={cn(
                            "rounded-md px-7 py-3.5 text-sm font-semibold transition-colors",
                            buttonIndex === 0 || button.variant === "primary"
                              ? "bg-gold-500 text-navy-900 hover:bg-gold-400"
                              : "text-cream-100 ring-1 ring-inset ring-cream-100/40 backdrop-blur-sm hover:bg-cream-100/10",
                          )}
                        >
                          {button.label}
                        </Link>
                      ))}
                    </div>
                  )}

                  {content.highlights.length > 0 && (
                    <ul className="mt-10 flex flex-wrap gap-x-7 gap-y-3">
                      {content.highlights.map((highlight) => (
                        <li
                          key={highlight}
                          className="flex items-center gap-2 text-sm text-navy-200"
                        >
                          <Check aria-hidden className="size-4 shrink-0 text-gold-400" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {slide.caption && (
                  <p className="absolute bottom-6 right-5 hidden max-w-xs text-right text-[11px] leading-relaxed text-navy-300 sm:right-8 lg:block">
                    {slide.caption}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/*
        The live region announces the change for a screen reader. `polite` so it
        waits for a pause rather than interrupting whatever is being read.
      */}
      {!single && (
        <p id={regionId} aria-live="polite" className="sr-only">
          Slide {index + 1} of {count}: {slides[index]?.heading}
        </p>
      )}

      {/*
        The controls sit at the top of the frame, not the bottom.

        A hero of this height is routinely taller than the space under the
        header — around 910px against roughly 740px on a 1440x820 window — so
        anything pinned to its bottom edge starts life below the fold, and the
        visitor has to scroll before they can see there are other slides at all.
        A carousel whose controls only appear once you have scrolled past it is
        a carousel most people never operate.

        Top placement is a fixed 1.5rem from the frame's own top edge, so it
        holds whatever the copy does and at every viewport height, rather than
        depending on the hero being short enough to fit. It also keeps the stop
        control in reach, which is what WCAG 2.2.2 asks of auto-moving content.
      */}
      {!single && (content.showArrows || content.showDots) && (
        <div className="container-site pointer-events-none absolute inset-x-0 top-6 z-20">
          <div className="flex items-center justify-between gap-4">
            {content.showDots ? (
              <div className="pointer-events-auto flex items-center gap-2.5">
                {slides.map((slide, dotIndex) => (
                  <button
                    key={`dot-${dotIndex}`}
                    type="button"
                    onClick={() => goTo(dotIndex)}
                    aria-current={dotIndex === index}
                    className="group py-2"
                  >
                    <span
                      className={cn(
                        "block h-0.5 transition-all",
                        dotIndex === index
                          ? "w-10 bg-gold-400"
                          : "w-5 bg-cream-100/40 group-hover:bg-cream-100/70",
                      )}
                    />
                    <span className="sr-only">
                      Go to slide {dotIndex + 1}: {slide.heading}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <span />
            )}

            <div className="pointer-events-auto flex items-center gap-2">
              {autoplay && (
                <button
                  type="button"
                  onClick={() => setPaused((value) => !value)}
                  className="inline-flex size-10 items-center justify-center rounded-full text-cream-100 ring-1 ring-inset ring-cream-100/30 transition-colors hover:bg-cream-100/10"
                >
                  {paused ? (
                    <Play aria-hidden className="size-4" />
                  ) : (
                    <Pause aria-hidden className="size-4" />
                  )}
                  <span className="sr-only">
                    {paused ? "Resume automatic slides" : "Stop automatic slides"}
                  </span>
                </button>
              )}

              {content.showArrows && (
                <>
                  <button
                    type="button"
                    onClick={() => goTo(index - 1)}
                    className="inline-flex size-10 items-center justify-center rounded-full text-cream-100 ring-1 ring-inset ring-cream-100/30 transition-colors hover:bg-cream-100/10"
                  >
                    <ChevronLeft aria-hidden className="size-5" />
                    <span className="sr-only">Previous slide</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => goTo(index + 1)}
                    className="inline-flex size-10 items-center justify-center rounded-full text-cream-100 ring-1 ring-inset ring-cream-100/30 transition-colors hover:bg-cream-100/10"
                  >
                    <ChevronRight aria-hidden className="size-5" />
                    <span className="sr-only">Next slide</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
